<template>
  <section class="mx-4 py-2">
    <v-row>
      <v-col cols="12">
        <v-card class="d-flex flex-column chart-card" elevation="0">
          <v-tabs v-model="activeTab" background-color="transparent" height="42" slider-size="2"
            color="primary" class="px-2">
            <v-tab>{{ $t('flowSightPortfolio.tabClients') }}</v-tab>
            <v-tab>{{ $t('flowSightPortfolio.tabRanking') }}</v-tab>
          </v-tabs>
          <v-divider></v-divider>

          <!-- v-show, not v-if: both panels are hand-drawn SVG held in a ref and
               registered as pseudo-charts in the dc group. Unmounting one would
               drop its host node and force a rebuild on every tab switch. The
               cost is that the hidden panel measures clientWidth 0, so the tab
               watcher redraws whichever became visible. -->
          <div v-show="activeTab === 0">
            <div class="d-flex align-center px-4 pt-3">
              <div class="card-title mr-3">{{ $t('flowSightSpot.portfolioTitle') }}</div>
              <div class="legend">
                <span class="dot buy"></span>{{ $t('flowSight.buy') }}
                <span class="dot sell ml-3"></span>{{ $t('flowSight.sell') }}
                <span class="tick ml-3"></span>{{ $t('flowSightSpot.avgHour') }}
              </div>
            </div>
            <div class="px-4 pb-2 hint">{{ $t('flowSightSpot.portfolioHint') }}</div>
            <div ref="panel" class="portfolio-scroll px-4 pb-4"></div>
          </div>

          <div v-show="activeTab === 1">
            <div class="d-flex align-center px-4 pt-3">
              <div class="card-title mr-3">{{ $t('flowSightDealers.title') }}</div>
              <div class="legend">
                <span v-for="(band, i) in maturityBands" :key="band" class="ml-2">
                  <span class="swatch" :style="{ background: bandColor(i) }"></span>{{ band }}
                </span>
              </div>
            </div>
            <div class="d-flex align-center px-4 pb-2">
              <div class="hint flex-grow-1">{{ $t('flowSightSpot.dealersHint') }}</div>
              <span class="hint mr-2">{{ $t('flowSightDealers.topN') }}</span>
              <v-btn-toggle v-model="topN" mandatory dense class="topn-toggle">
                <v-btn v-for="opt in topNOptions" :key="String(opt.value)" :value="opt.value" small>
                  {{ opt.label }}
                </v-btn>
              </v-btn-toggle>
            </div>
            <div ref="dealers" class="portfolio-scroll px-4 pb-4"></div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import crossfilter from "crossfilter2";
import * as dc from "dc";
import "dc/dist/style/dc.css";

import { useAnalyticsProStore } from "@/store/analyticsStore";
import { mapStores } from "pinia";
import { safeRedrawAll, createScheduledRedraw, createResizeWatcher } from "./runtime/chartLifecycle.js";
import { createClientPortfolioPanel } from "./portfolio/createClientPortfolioPanel.js";
import { createDealerRankingPanel } from "./portfolio/createDealerRankingPanel.js";
import { HOUR_LABELS } from "@/utils/flowSightHourBuckets";
import * as d3 from "d3";
import { createClientPortfolioReducer } from "./calculations/clientPortfolio.js";
import { isNegotiated } from "./calculations/calculateNegotiatedVolumeBRL.js";
import { toDate } from "@/utils/flowSightPeriods";

const CHART_GROUP = "flowsight-portfolio";

export default {
  data() {
    return {
      ndx: null,
      dim: {},
      panel: null,
      dealers: null,
      activeTab: 0,
      // See Conversions.vue - generation of the dataset this page built from.
      builtGeneration: -1,
      // null = all. View preference, so it stays local to the page.
      topN: 12,
      isActive: false,
    };
  },
  computed: {
    ...mapStores(useAnalyticsProStore),
    // Same rule the Overview uses for its charts: one side selected paints in
    // that side's colour, both selected is the neutral green.
    maturityBands() {
      return HOUR_LABELS;
    },
    topNOptions() {
      return [12, 24, 36, 48].map((n) => ({ value: n, label: String(n) }))
        .concat([{ value: null, label: this.$t('flowSightDealers.topNAll') }]);
    },
    sideColor() {
      const arr = this.analyticsProStore.sideFilter || [];
      if (arr.length !== 1) return '#2ecc71';
      return (arr[0] || '').toUpperCase() === 'BUY' ? '#27c1e6' : '#ffb300';
    },
  },
  watch: {
    'analyticsProStore.datasetGeneration': {
      immediate: false,
      handler() {
        this.$nextTick(() => this.initPipeline());
      },
    },
    'analyticsProStore.sideFilter': {
      handler(arr) { if (this.isActive) this._applySide(arr); },
    },
    'analyticsProStore.counterpartyFilter': {
      handler(names) { if (this.isActive) this._applyCounterparty(names); },
    },
    'analyticsProStore.rfqPeriod': {
      handler() { if (this.isActive) this._applyRfqPeriod(); },
    },
    'analyticsProStore.rfqCustomRange': {
      deep: true,
      handler() { if (this.isActive) this._applyRfqPeriod(); },
    },
    'analyticsProStore.activeFilter': {
      handler(val) { this._applyTenor(val); },
    },
    'analyticsProStore.periodSelection': {
      deep: true,
      handler() { if (this.isActive) this._applyTenor('TENOR'); },
    },
    // The panel that was hidden measured clientWidth 0 and drew at its fallback
    // width, so it has to redraw once it is on screen.
    activeTab() {
      this.$nextTick(() => safeRedrawAll(CHART_GROUP));
    },
    topN() {
      this.scheduleRedraw();
    },
  },
  mounted() {
    this._resizeWatcher = createResizeWatcher(this.onResize);
    if (this.$refs.panel) this._resizeWatcher.observe(this.$refs.panel);
    if (this.$refs.dealers) this._resizeWatcher.observe(this.$refs.dealers);
    this.$nextTick(() => this.initPipeline());
  },
  activated() {
    this.isActive = true;
    this.$nextTick(() => {
      if (!this.ndx
        || dc.chartRegistry.list(CHART_GROUP).length === 0
        || this.builtGeneration !== this.analyticsProStore.getDatasetGeneration) {
        this.initPipeline();
        return;
      }
      this._applySide(this.analyticsProStore.sideFilter);
      this._applyCounterparty(this.analyticsProStore.counterpartyFilter);
      this._applyRfqPeriod();
      this._applyTenor(this.analyticsProStore.activeFilter);
      this.scheduleRedraw();
    });
  },
  deactivated() {
    this.isActive = false;
  },
  beforeDestroy() {
    this._resizeWatcher?.disconnect();
    try { dc.deregisterAllCharts(CHART_GROUP); } catch (_) {}
  },
  methods: {
    // Mirrors bandColor in createDealerRankingPanel so the legend cannot drift
    // from the bars.
    bandColor(i) {
      return d3.interpolateBlues(0.25 + (i / (HOUR_LABELS.length - 1)) * 0.6);
    },
    // Same universe as the Conversions tab: an RFQ the client actually decided
    // on. Portfolio volume is NEGOTIATED (DEAL + NOTH.DONE).
    _rows() {
      const rows = this.analyticsProStore.getAnalytics;
      return Array.isArray(rows) ? rows.filter(isNegotiated) : [];
    },
    initPipeline() {
      const rows = this._rows();
      if (!this.$refs.panel) return;
      // Same reason as Conversions: a detached panel measures 0 and the SVG
      // would be laid out against the fallback width.
      if (!(this.$refs.panel.clientWidth > 0)) return;

      try { dc.deregisterAllCharts(CHART_GROUP); } catch (_) {}
      this.$refs.panel.innerHTML = "";
      if (this.$refs.dealers) this.$refs.dealers.innerHTML = "";

      this.builtGeneration = this.analyticsProStore.getDatasetGeneration;
      this.ndx = crossfilter(rows);
      this.buildDimensions();

      this.panel = createClientPortfolioPanel({
        el: this.$refs.panel,
        chartGroup: CHART_GROUP,
        dim: this.dim.dimClient,
        group: this.grpClient,
        t: (key) => this.$t(key),
        uid: this._uid,
        // Read at draw time so flipping BUY/SELL recolours on the next redraw
        // without rebuilding the panel.
        sideColor: () => this.sideColor,
        // A crossfilter group cannot hand back the records behind it, so the
        // per-transaction circles read the filtered rows directly.
        rowsProvider: () => (this.ndx ? this.ndx.allFiltered() : []),
        amountOf: (d) => this.analyticsProStore.convertNotionalToBRL(d) || 0,
      });

      // Both panels read the SAME group - one pass over the rows feeds the
      // per-client rows above and the ranking below.
      this.dealers = createDealerRankingPanel({
        el: this.$refs.dealers,
        chartGroup: CHART_GROUP,
        group: this.grpClient,
        t: (key) => this.$t(key),
        uid: this._uid,
        // Read at draw time, so changing it only costs a redraw.
        topN: () => this.topN,
      });

      this._applySide(this.analyticsProStore.sideFilter);
      this._applyCounterparty(this.analyticsProStore.counterpartyFilter);
      this._applyRfqPeriod();
      this._applyTenor(this.analyticsProStore.activeFilter);
      this.scheduleRedraw();
    },
    buildDimensions() {
      // The panel reads THIS group, so it must not be the dimension the panel
      // itself filters - it doesn't filter, so a plain dimension is fine.
      const dimClient = this.ndx.dimension((d) => String(d.counterparty || "").trim());
      const reducer = createClientPortfolioReducer(
        (d) => this.analyticsProStore.convertNotionalToBRL(d) || 0
      );
      this.grpClient = dimClient.group().reduce(reducer.add, reducer.rem, reducer.init);

      // Global sidebar filters. Separate dimensions from dimClient so each one
      // keeps working while the others are applied.
      const dimSide = this.ndx.dimension((d) => d.side);
      const dimCounterparty = this.ndx.dimension((d) => String(d.counterparty || "").trim());
      const dimRfqDate = this.ndx.dimension((d) => toDate(d.rfqtimestamp) || new Date(0));
      const dimTenorDays = this.ndx.dimension((d) => d.tenorDays);

      this.dim = { dimClient, dimSide, dimCounterparty, dimRfqDate, dimTenorDays };
    },
    _applySide(arr = []) {
      const { dimSide } = this.dim || {};
      if (!dimSide) return;
      dimSide.filterAll();
      if (arr.length === 1) {
        const side = (arr[0] || "").toUpperCase();
        dimSide.filterFunction((d) => (d || "").toUpperCase() === side);
      }
      this.scheduleRedraw();
    },
    _applyCounterparty(names = []) {
      const { dimCounterparty } = this.dim || {};
      if (!dimCounterparty) return;
      dimCounterparty.filterAll();
      // Empty means NONE now - the sidebar ticks every name by default.
      const selected = new Set(names);
      dimCounterparty.filterFunction((d) => selected.has(d));
      this.scheduleRedraw();
    },
    _applyRfqPeriod() {
      const { dimRfqDate } = this.dim || {};
      if (!dimRfqDate) return;
      const window = this.analyticsProStore.getRfqPeriodWindow;
      dimRfqDate.filterAll();
      if (window) dimRfqDate.filterFunction((d) => d >= window.from && d <= window.to);
      this.scheduleRedraw();
    },
    _applyTenor(type) {
      const { dimTenorDays } = this.dim || {};
      if (!dimTenorDays) return;
      dimTenorDays.filterAll();
      const range = this.analyticsProStore.getDateRange(
        type,
        this.analyticsProStore.customStart,
        this.analyticsProStore.customEnd
      );
      if (range) dimTenorDays.filterFunction((d) => d >= range[0] && d <= range[1]);
      this.scheduleRedraw();
    },
    scheduleRedraw() {
      if (!this._scheduleRedrawFn) {
        this._scheduleRedrawFn = createScheduledRedraw(CHART_GROUP, () => true);
      }
      this._scheduleRedrawFn();
    },
    onResize() {
      if (!this.panel) return;
      safeRedrawAll(CHART_GROUP);
    },
  },
};
</script>

<style scoped>
.chart-card {
  background-color: rgb(18, 19, 25);
  border: 1px solid rgb(33, 36, 46);
  border-radius: 8px;
}
.card-title {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
}
.legend {
  font-size: 11px;
  color: var(--muted);
  display: flex;
  align-items: center;
}
.hint {
  font-size: 11px;
  color: var(--muted);
}
.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}
.topn-toggle ::v-deep .v-btn {
  font-size: 10px;
  letter-spacing: 0;
  min-width: 34px !important;
  padding: 0 8px !important;
}
.swatch {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  display: inline-block;
  margin-right: 4px;
}
.dot.buy { background: #27c1e6; }
.dot.sell { background: #ffb300; }
.tick {
  width: 3px;
  height: 12px;
  background: #f2f2f2;
  display: inline-block;
  margin-right: 5px;
}
/*
  Sem rolagem propria. `.flowsight-content` ja e um container rolavel, entao o
  `max-height: 70vh; overflow-y: auto` que existia aqui criava um SEGUNDO nivel
  de rolagem aninhado: a roda ficava presa no de dentro e as ~4.400px de painel
  (58 contrapartes x 76px) eram navegadas por uma fresta. O SVG sempre foi
  desenhado inteiro - o container so recortava - entao soltar nao custa render.
*/
.portfolio-scroll {
  /* Nenhum `overflow` aqui, e isso e deliberado: `overflow-x: hidden` sozinho
     faz o `overflow-y: visible` computar para `auto` (CSS Overflow 3, §3.2), o
     que transformaria esta div num container de rolagem. O eixo abaixo grudaria
     NELA - que nunca rola, porque nao tem mais altura maxima - e o sticky nunca
     engataria. O recorte horizontal, se o painel passar da largura, vira
     rolagem da area de conteudo, que e melhor que cortar o grafico em silencio. */
}

/*
  O eixo gruda no topo da area rolavel. Os dois filhos abaixo sao criados por
  createClientPortfolioPanel/createDealerRankingPanel via document.createElement,
  fora do template, entao nao carregam o atributo de escopo - dai o ::v-deep.
  O fundo e obrigatorio: sem ele as linhas passam por baixo do eixo e o texto
  fica ilegivel. Tem que ser o mesmo do .chart-card.
*/
::v-deep .portfolio-axis {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(18, 19, 25);
}
</style>
